import { isEqual, isNil } from 'lodash';
import { MBStorage } from '@shared/util/storage';
import { JsonStorage } from '@shared/util/storage/JsonStorage';
import { CommonFields, Fields } from '../types';

const ONE_DAY_TTL = 1000 * 60 * 60 * 24;

export type Values = Fields & CommonFields;

export async function getCardDraft(
  channelId: number | undefined,
  cardId?: string,
): Promise<Values | undefined> {
  if (isNil(channelId)) {
    return undefined;
  }
  const key = getCardDraftStorageKey(channelId, cardId);
  return JsonStorage.get<Values | undefined>(key);
}

export async function saveCardDraft(
  cardFields: Values,
  channelId: number,
  cardId?: string,
): Promise<void> {
  const key = getCardDraftStorageKey(channelId, cardId);
  await JsonStorage.set(key, cardFields, ONE_DAY_TTL);
}

function getCardDraftStorageKey(channelId: number, cardId?: string) {
  return `card-draft-${channelId}-${cardId ?? 'new'}`;
}

/**
 * setExpirationForOldCardDrafts
 *
 * This function is used to set the expiration for old card drafts. It's called
 * on startup to ensure that old card drafts are not stored indefinitely. It
 * searches for old keys in localstorage that match the pattern
 * {channelId}-{create|update}-{cardId?}
 *
 * It then attempts to save the card draft to the new storage format with a
 * one-day TTL and remove the old key if successful.
 */

export const oldCardDraftRegex =
  /(\d+)-(create|update)-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})?/;

export async function setExpirationForOldCardDrafts() {
  const keys = await MBStorage.keys();
  const draftKeys = keys.filter(key => oldCardDraftRegex.test(key));
  for (const key of draftKeys) {
    const matches = key.match(oldCardDraftRegex);
    const [, channelId, , cardId] = matches ?? [];
    const value = await MBStorage.get(key);
    if (isNil(channelId) || isNil(value)) {
      continue;
    }
    try {
      const numericChannelId = Number(channelId);
      const parsed = JSON.parse(value);
      await saveCardDraft(parsed, numericChannelId, cardId);
      const stored = await getCardDraft(numericChannelId, cardId);
      const wasStoredCorrectly = isEqual(parsed, stored);
      if (wasStoredCorrectly) {
        await MBStorage.remove(key);
      }
    } catch (e) {
      console.error(e);
    }
  }
}
